import { useEffect, useState, type FormEvent } from "react";
import { useAuthAttemptMutation } from "hooks/useAuthAttemptMutation";
import { beginReauthenticationAttempt } from "lib/auth-attempt";
import { BiShield } from "react-icons/bi";
import { Button } from "components/common";
import { Modal } from "components/common/modal/Modal";
import { ModalBody } from "components/common/modal/ModalBody/ModalBody";
import { ModalFooter } from "components/common/modal/ModalFooter/ModalFooter";
import { ModalHeader } from "components/common/modal/ModalHeader/ModalHeader";
import { InputV3 } from "components/common/input/InputV3";
import {
  completeBetterAuthMfaReauthentication,
  reauthenticateBetterAuthSession,
} from "lib/better-auth-reauthentication";
import { ServerError } from "utils/genericUtilityFunctions";
import type { ReauthenticateModalProps } from "./ReauthenticateModal.types";

export type { ReauthenticateModalProps } from "./ReauthenticateModal.types";

export const ReauthenticateModal = ({
  email,
  isOpen,
  onClose,
  onSuccess,
  title = "Confirm your password",
  subtitle = "For your security, sign in again to continue.",
}: ReauthenticateModalProps) => {
  const [password, setPassword] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [step, setStep] = useState<"password" | "twoFactor">("password");
  const [error, setError] = useState<string | undefined>();

  const reauthenticate = useAuthAttemptMutation(
    (_variables: void, attempt) =>
      step === "password"
        ? reauthenticateBetterAuthSession(email, password, attempt)
        : completeBetterAuthMfaReauthentication(verificationCode, attempt),
    {
      onSuccess: () => {
        setPassword("");
        setVerificationCode("");
        setStep("password");
        setError(undefined);
        onSuccess();
      },
      onError: (mutationError: unknown) => {
        if (mutationError instanceof ServerError && mutationError.code === "TWO_FACTOR_REQUIRED") {
          setPassword("");
          setError(undefined);
          setStep("twoFactor");
          return;
        }

        setError(
          mutationError instanceof ServerError
            ? mutationError.message
            : "Unable to verify your password. Please try again."
        );
      },
    },
    beginReauthenticationAttempt
  );

  useEffect(() => {
    if (!isOpen) reauthenticate.reset();
  }, [isOpen, reauthenticate.reset]);

  const handleClose = () => {
    reauthenticate.reset();
    setPassword("");
    setVerificationCode("");
    setStep("password");
    setError(undefined);
    onClose();
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const value = step === "password" ? password : verificationCode;
    if (value.trim()) {
      reauthenticate.mutate();
    }
  };

  const isPasswordStep = step === "password";
  const value = isPasswordStep ? password : verificationCode;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} maxWidth="max-w-md">
      <ModalHeader
        title={isPasswordStep ? title : "Enter your authentication code"}
        subtitle={
          isPasswordStep ? subtitle : "Enter the code from your authenticator app to continue."
        }
        icon={<BiShield className="text-hue-emerald" />}
        onClose={handleClose}
      />
      <form onSubmit={handleSubmit} className="flex flex-col gap-6">
        <ModalBody showBorders={false}>
          {isPasswordStep ? (
            <InputV3
              label="Current password"
              type="password"
              value={password}
              onChange={(event) => {
                setPassword(event.target.value);
                if (error) {
                  setError(undefined);
                }
              }}
              autoComplete="current-password"
              error={Boolean(error)}
              errorMessage={error}
            />
          ) : (
            <InputV3
              label="Authentication code"
              value={verificationCode}
              onChange={(event) => {
                setVerificationCode(event.target.value);
                if (error) {
                  setError(undefined);
                }
              }}
              inputMode="numeric"
              autoComplete="one-time-code"
              error={Boolean(error)}
              errorMessage={error}
            />
          )}
        </ModalBody>
        <ModalFooter>
          <Button type="button" onClick={handleClose} variant="badge" color="gray">
            Cancel
          </Button>
          <Button
            type="submit"
            variant="badge"
            color="emerald"
            isLoading={reauthenticate.isPending}
            disabled={!email || !value.trim() || reauthenticate.isPending}
          >
            Continue
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
};
